import { IonRangeCustomEvent, RangeChangeEventDetail } from '@ionic/core';
import { IonContent } from '@ionic/react';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { UiTypography } from '@/components/ui/Typography';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { NavigationRange } from '@/pages/Onboarding/components/NavigationRange';
import { useOnboarding } from '@/pages/Onboarding/context';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { useLayoutFormSelections } from '../../hooks/useLayoutFormSelections';

import AffectionateIcon from './icons/smile-affectionate.png';
import DominantIcon from './icons/smile-dominant.png';
import EmotionalIcon from './icons/smile-emotional.png';
import FlirtyIcon from './icons/smile-flirty.png';
import IndependentIcon from './icons/smile-independent.png';
import LogicalIcon from './icons/smile-logical.png';
import SeriousIcon from './icons/smile-serious.png';
import ShyIcon from './icons/smile-shy.png';
import {
  PersonalityAiIconStyles,
  PersonalityRangeItemStyles,
  PersonalityRangeStyles,
  PersonalitySmileStyles,
  PersonalityStyles,
  PersonalityTitleStyles,
} from './Personality.styles';

import type { LayoutDataType } from '../../types';

export const ScreenPersonality: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Personality',
      });
    }
  }, []);

  const shouldAnimate = webCampaign.isAnimated();
  const [showElements, setShowElements] = React.useState<boolean>(true);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();
  const personality = form.watch('personality');

  const { ageGeneration, bodyShape, hairColor } = useLayoutFormSelections();
  React.useEffect(() => {
    preloadImage(
      `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-second/${ageGeneration}/${bodyShape}/${hairColor}/send_photos.jpg`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNextStep = (): void => {
    form.setValue('personality', personality);
    userService.createOrUpdate({
      onboarding_data: {
        personality,
      },
    });

    if (shouldAnimate) {
      setTimeout(() => {
        setShowElements(false);
        nextStepTimeout(wizard.nextStep);
      }, 300);
    } else {
      wizard.nextStep();
    }
  };

  const handleBackStep = (): void => {
    if (shouldAnimate) {
      setShowElements(false);
      nextStepTimeout(() => wizard.goToStep('ideal_match'));
    } else {
      wizard.goToStep('ideal_match');
    }
  };

  return (
    <PersonalityStyles background="dark-violet">
      <NavigationRange onBack={handleBackStep} />

      <IonContent>
        <PersonalityAiIconStyles>
          <img
            src={`https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-second/${ageGeneration}/${bodyShape}/${hairColor}/personality.jpg`}
            alt="ai acon"
          />
        </PersonalityAiIconStyles>

        <PersonalityTitleStyles animate={shouldAnimate} show={showElements}>
          What kind of personality attracts you more?
        </PersonalityTitleStyles>

        <PersonalityRangeItem
          left={{
            label: 'Shy',
            icon: ShyIcon,
          }}
          right={{
            label: 'Dominant',
            icon: DominantIcon,
          }}
          value={personality.shy_dominant}
          onChange={({ detail }) => {
            form.setValue('personality.shy_dominant', detail.value as number);
          }}
          show={showElements}
          animate={shouldAnimate}
        />

        <PersonalityRangeItem
          left={{
            label: 'Serious',
            icon: SeriousIcon,
          }}
          right={{
            label: 'Flirty',
            icon: FlirtyIcon,
          }}
          value={personality.serious_flirty}
          onChange={({ detail }) => {
            form.setValue('personality.serious_flirty', detail.value as number);
          }}
          show={showElements}
          animate={shouldAnimate}
        />

        <PersonalityRangeItem
          left={{
            label: 'Logical',
            icon: LogicalIcon,
          }}
          right={{
            label: 'Emotional',
            icon: EmotionalIcon,
          }}
          value={personality.logical_emotional}
          onChange={({ detail }) => {
            form.setValue(
              'personality.logical_emotional',
              detail.value as number,
            );
          }}
          show={showElements}
          animate={shouldAnimate}
        />

        <PersonalityRangeItem
          left={{
            label: 'Affectionate',
            icon: AffectionateIcon,
          }}
          right={{
            label: 'Independent',
            icon: IndependentIcon,
          }}
          value={personality.affectionate_independent}
          onChange={({ detail }) => {
            form.setValue(
              'personality.affectionate_independent',
              detail.value as number,
            );
          }}
          show={showElements}
          animate={shouldAnimate}
        />
      </IonContent>

      <OnboardingFooter buttonColor="pink" onContinue={handleNextStep} />
    </PersonalityStyles>
  );
};

function PersonalityRangeItem({
  left,
  right,
  value,
  onChange,
  animate,
  show,
}: {
  left: {
    label: string;
    icon: string;
  };
  right: {
    label: string;
    icon: string;
  };
  value: number;
  onChange: (event: IonRangeCustomEvent<RangeChangeEventDetail>) => void;
  animate?: boolean;
  show: boolean;
}) {
  const shouldReduceMotion = useReducedMotion();
  const canAnimate = animate && !shouldReduceMotion;

  if (canAnimate) {
    return (
      <AnimatePresence>
        {show && (
          <PersonalityRangeItemStyles
            variants={{
              hidden: { opacity: 0, y: '100%' },
              visible: { opacity: 1, y: 0 },
            }}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <div className="range-labels">
              <UiTypography variant="label-large" className="range-label">
                <PersonalitySmileStyles src={left.icon} />
                {left.label}
              </UiTypography>
              <UiTypography variant="label-large" className="range-label">
                {right.label}
                <PersonalitySmileStyles src={right.icon} />
              </UiTypography>
            </div>
            <PersonalityRangeStyles
              value={value}
              ticks={false}
              defaultValue={50}
              snaps={false}
              onIonChange={onChange}
            />
          </PersonalityRangeItemStyles>
        )}
      </AnimatePresence>
    );
  }

  return (
    <PersonalityRangeItemStyles>
      <div className="range-labels">
        <UiTypography variant="label-large" className="range-label">
          <PersonalitySmileStyles src={left.icon} />
          {left.label}
        </UiTypography>
        <UiTypography variant="label-large" className="range-label">
          {right.label}
          <PersonalitySmileStyles src={right.icon} />
        </UiTypography>
      </div>
      <PersonalityRangeStyles
        value={value}
        ticks={false}
        defaultValue={50}
        snaps={false}
        onIonChange={onChange}
      />
    </PersonalityRangeItemStyles>
  );
}
